import type { ServerMessage } from '../shared/protocol';
import type { BidValue, ClientView } from '../shared/types';
import { el, mount } from './dom';
import { TurnSignal } from './notify';
import { renderBidding } from './render/bidding';
import { renderHostPage } from './render/host';
import { renderLobby } from './render/lobby';
import {
  renderBidHistory,
  renderPlayers,
  renderReveal,
} from './render/table';
import { Connection, type ConnectionStatus } from './ws';

import './styles.css';

const root = document.querySelector<HTMLElement>('#app');

type AppState = {
  view: ClientView | null;
  status: ConnectionStatus;
  /** Bid being composed. Reset whenever the current bid changes under us. */
  draft: BidValue | null;
  draftFor: string | null;
  /** Uncommitted name text; see renderLobby. */
  nameDraft: string | null;
  error: string | null;
  /** Transient explanation of something the server did on its own. */
  notice: string | null;
};

const state: AppState = {
  view: null,
  status: 'connecting',
  draft: null,
  draftFor: null,
  nameDraft: null,
  error: null,
  notice: null,
};

const signal = new TurnSignal();

function main(): void {
  if (root === null) return;

  const table = location.pathname.match(/^\/t\/([^/]+)\/?$/);
  if (table === null) {
    renderHostPage(root);
    return;
  }

  void joinTable(root, decodeURIComponent(table[1] ?? ''));
}

/**
 * Claim the seat, then open the socket.
 *
 * The invite token lives in the URL fragment (§10), so it never reached the
 * server as part of the request line. It is stripped from the address bar as
 * soon as it has been exchanged, so it does not linger in screen shares, browser
 * history, or a screenshot of someone's tab.
 */
async function joinTable(container: HTMLElement, tableId: string): Promise<void> {
  const token = location.hash.startsWith('#') ? location.hash.slice(1) : '';

  if (token.length > 0) {
    history.replaceState(null, '', location.pathname);

    const claimed = await fetch('/api/claim', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    }).catch(() => null);

    if (claimed !== null && !claimed.ok) {
      const body = (await claimed.json().catch(() => ({}))) as { code?: string };
      showFatal(
        container,
        body.code === 'SEAT_BUSY'
          ? 'Somebody is already using that invite link.'
          : body.code === 'WRONG_PHASE'
            ? 'That game has already started.'
            : 'That invite link is not valid any more.',
        body.code === 'SEAT_BUSY'
          ? 'If that was you on another device, close the game there and wait two minutes.'
          : 'Ask the host for a new link.',
      );
      return;
    }
  }

  const connection = new Connection(tableId, {
    onMessage: (message) => {
      handleMessage(message);
      render(container, connection);
    },
    onStatus: (status) => {
      state.status = status;
      render(container, connection);
    },
  });

  connection.open();
  render(container, connection);
}

function handleMessage(message: ServerMessage): void {
  if (message.type === 'view') {
    const { type: _type, ...view } = message;
    const next = view as ClientView;

    // Drop a half-composed bid whenever the bid it was built against changed.
    const signature = `${next.roundNumber}:${next.currentBid?.quantity ?? '-'}:${
      next.currentBid?.face ?? '-'
    }`;
    if (signature !== state.draftFor) {
      state.draft = null;
      state.draftFor = signature;
    }
    if (next.roundNumber !== state.view?.roundNumber) state.notice = null;

    state.view = next;
    state.error = null;
    signal.update(
      next.activePlayerId === next.you.id &&
        (next.phase === 'bidding' || next.phase === 'revealing'),
    );
    return;
  }

  if (message.type === 'error') {
    state.error = message.message;
    return;
  }

  // A turn that ran out is played for you (§9). Without saying so, the board
  // just changes on its own and looks like a glitch.
  if (message.type === 'event' && message.kind === 'turnTimeout') {
    const who = state.view?.players.find((p) => p.id === message.playerId);
    state.notice =
      message.playerId === state.view?.you.id
        ? 'You ran out of time — the minimum raise was bid for you.'
        : `${who?.name ?? 'A player'} ran out of time.`;
  }
}

function render(container: HTMLElement, connection: Connection): void {
  const view = state.view;

  if (view === null) {
    mount(
      container,
      el('div', { class: 'page' }, [
        el('h1', { text: "Liar's Dice" }),
        el('p', {
          class: 'hint',
          role: 'status',
          text:
            state.status === 'closed'
              ? 'Disconnected. Reconnecting…'
              : 'Connecting…',
        }),
      ]),
    );
    return;
  }

  const page = el('div', { class: 'page' });
  page.appendChild(renderHeader(view));

  if (state.status !== 'open') {
    page.appendChild(
      el('p', {
        class: 'banner banner--warn',
        role: 'status',
        text: 'Connection lost — reconnecting…',
      }),
    );
  }

  if (state.error !== null) {
    page.appendChild(
      el('p', { class: 'banner banner--error', role: 'alert', text: state.error }),
    );
  }

  if (state.notice !== null) {
    page.appendChild(
      el('p', { class: 'banner banner--warn', role: 'status', text: state.notice }),
    );
  }

  if (view.phase === 'lobby') {
    page.appendChild(
      renderLobby(view, state.nameDraft, {
        onName: (name) => {
          state.nameDraft = null;
          connection.send({ type: 'setName', name });
        },
        onNameInput: (value) => {
          state.nameDraft = value;
        },
        onReady: (ready) => connection.send({ type: 'ready', ready }),
        onStart: () => connection.send({ type: 'start' }),
      }),
    );
    page.appendChild(renderPlayers(view));
    swap(container, page);
    return;
  }

  const reveal = renderReveal(view);
  if (reveal !== null) page.appendChild(reveal);

  if (view.phase === 'gameOver') {
    const winner = view.players.find((p) => p.id === view.winnerId);
    page.appendChild(
      el('section', { class: 'gameover', 'aria-live': 'polite' }, [
        el('h2', {
          text:
            view.winnerId === view.you.id
              ? 'You win.'
              : `${winner?.name ?? 'Nobody'} wins.`,
        }),
      ]),
    );
  }

  page.appendChild(renderPlayers(view));

  if (view.phase === 'bidding') {
    page.appendChild(renderBidHistory(view));
    page.appendChild(
      renderBidding(
        view,
        state.draft,
        (draft) => {
          state.draft = draft;
          render(container, connection);
        },
        {
          onBid: (bid) =>
            connection.send({ type: 'bid', quantity: bid.quantity, face: bid.face }),
          onChallenge: () => connection.send({ type: 'challenge' }),
          onExact: () => connection.send({ type: 'exact' }),
        },
      ),
    );
  }

  if (view.phase === 'revealing' && view.activePlayerId === view.you.id) {
    const next = el('button', {
      type: 'button',
      id: 'deal-next',
      class: 'button button--primary',
      text: 'Deal the next round',
    });
    next.addEventListener('click', () => connection.send({ type: 'continue' }));
    page.appendChild(el('div', { class: 'bidding__actions' }, [next]));
  } else if (view.phase === 'revealing') {
    const starter = view.players.find((p) => p.id === view.activePlayerId);
    page.appendChild(
      el('p', {
        class: 'hint',
        text: `Waiting for ${starter?.name ?? 'the next player'} to deal…`,
      }),
    );
  }

  swap(container, page);
}

/**
 * Replace the page, keeping keyboard focus and the caret where they were.
 *
 * Every server broadcast re-renders everything — the state is tiny and that
 * keeps rendering honest. But a naive teardown means somebody else readying up
 * detaches the node you were about to click and steals focus out of the box you
 * were typing in. Re-finding the focused element by id afterwards costs nothing
 * and makes the full re-render invisible.
 */
function swap(container: HTMLElement, page: HTMLElement): void {
  const active = document.activeElement;
  const id = active instanceof HTMLElement ? active.id : '';
  const input = active instanceof HTMLInputElement ? active : null;
  const start = input?.selectionStart ?? null;
  const end = input?.selectionEnd ?? null;

  mount(container, page);

  if (id === '') return;
  const restored = document.getElementById(id);
  if (restored === null) return;

  restored.focus();
  if (restored instanceof HTMLInputElement && start !== null) {
    try {
      restored.setSelectionRange(start, end ?? start);
    } catch {
      // Not all input types support selection ranges.
    }
  }
}

function renderHeader(view: ClientView): HTMLElement {
  const yourTurn =
    view.activePlayerId === view.you.id && view.phase === 'bidding';
  const active = view.players.find((p) => p.id === view.activePlayerId);

  const mute = el('button', {
    type: 'button',
    id: 'mute-toggle',
    class: 'button button--small',
    'aria-pressed': signal.muted ? 'true' : 'false',
    text: signal.muted ? 'Sound off' : 'Sound on',
  });
  mute.addEventListener('click', () => {
    signal.setMuted(!signal.muted);
    mute.textContent = signal.muted ? 'Sound off' : 'Sound on';
    mute.setAttribute('aria-pressed', signal.muted ? 'true' : 'false');
  });

  return el('header', { class: 'header' }, [
    el('div', { class: 'header__row' }, [
      el('h1', { class: 'header__title', text: "Liar's Dice" }),
      mute,
    ]),
    el('p', {
      class: `turn ${yourTurn ? 'turn--yours' : ''}`,
      role: 'status',
      // The one announcement a screen reader user must not miss.
      'aria-live': 'polite',
      text:
        view.phase === 'lobby'
          ? 'Waiting for players'
          : view.phase === 'gameOver'
            ? 'Game over'
            : view.phase === 'revealing'
              ? 'Dice revealed'
              : yourTurn
                ? 'Your turn'
                : `${active?.name ?? 'Someone'} is thinking…`,
    }),
    el('p', {
      class: 'header__round',
      text: view.phase === 'lobby' ? '' : `Round ${view.roundNumber}`,
    }),
    renderClock(view),
  ]);
}

/**
 * Seconds left on the current turn.
 *
 * A timeout auto-plays your turn (§9), so the clock has to be visible — being
 * silently auto-bid because a countdown existed but was never shown would feel
 * like a bug. Updated by its own interval rather than by re-rendering the page
 * every second, which would fight with focus and the bidding draft.
 */
function renderClock(view: ClientView): HTMLElement {
  const node = el('p', { class: 'clock', role: 'timer', 'aria-live': 'off' });

  const paint = (): boolean => {
    const deadline =
      view.phase === 'bidding'
        ? view.turnDeadline
        : view.phase === 'revealing'
          ? null
          : null;

    if (deadline === null) {
      node.textContent = '';
      return false;
    }

    const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
    const yours = view.activePlayerId === view.you.id;
    node.textContent = yours
      ? `${left}s to act`
      : `${left}s`;
    node.classList.toggle('clock--urgent', left <= 10);
    return left > 0;
  };

  if (!paint()) return node;

  const timer = setInterval(() => {
    // The node is replaced on the next broadcast; stop when it is detached.
    if (!node.isConnected || !paint()) clearInterval(timer);
  }, 1000);

  return node;
}

function showFatal(
  container: HTMLElement,
  title: string,
  detail: string,
): void {
  mount(
    container,
    el('div', { class: 'page' }, [
      el('h1', { text: "Liar's Dice" }),
      el('div', { class: 'banner banner--error', role: 'alert' }, [
        el('strong', { text: title }),
        el('p', { text: detail }),
      ]),
    ]),
  );
}

// Mobile WebKit will not start an AudioContext outside a user gesture, so the
// first click anywhere unlocks it — long before there is a turn to announce.
document.addEventListener('pointerdown', () => signal.unlockAudio(), {
  once: true,
});

main();
